import type { PreProposalData } from "../store/preProposalStore";
import { normalizeResidentialPhone2TipoToApiCode } from "@repo/core/utils/residentialPhone2.utils";
import { TIPO_RESIDENCIA } from "@repo/core/services/entities/Proposals";
import { pickFirstValidErpDate } from "@repo/core/utils/erpDate.utils";
import { normalizeBirthDateToBackendDate } from "@repo/core/utils/birthDate.utils";
import { sanitizeParentNameForApi } from "@repo/core/utils/parentName.utils";
import {
  areNormalizedPhonesEqual,
  joinPhoneDigits
} from "@repo/core/utils/phone.utils";

type ProposalWithLegacyResidentialPhone = Partial<PreProposalData> & {
  strResDdd?: string;
  strResFone?: string;
  strResTipoFone1?: string;
};

const stripUndefinedValues = <T extends Record<string, unknown>>(
  payload: T
): Partial<T> =>
  Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined)
  ) as Partial<T>;

/**
 * Removes local-only store control fields that must not be sent to ERP endpoints.
 */
export const sanitizePreProposalApiPayload = (
  proposal?: Partial<PreProposalData>
): Partial<PreProposalData> => {
  if (!proposal) {
    return {};
  }

  /* eslint-disable @typescript-eslint/no-unused-vars -- strip local-only / legacy keys */
  const {
    uploadedProposalDocumentTypes,
    unicoStatus,
    shouldForceManualCapture,
    restartRequired,
    shouldForceNewBiometry,
    biometryReuseProcessId,
    needsContextUpdate,
    unicoUploadedIdentityDocumentTypes,
    manualCaptureExcludedDocumentTypes,
    queueEditMode,
    strReanalise: _legacyStrReanalise,
    ...restAfterUiStrip
  } = proposal as Partial<PreProposalData> & { strReanalise?: string };

  const {
    strResDdd,
    strResFone,
    strResTipoFone1,
    strResDDD2,
    strResFone2,
    strResTipoFone2,
    datAtivDataAdmissao: rawDatAtivDataAdmissao,
    strAtivDataAdmissao,
    datTrabDesde,
    strTrabalhaDesde,
    datNascimento: rawDatNascimento,
    ...apiPayload
  } = restAfterUiStrip as ProposalWithLegacyResidentialPhone;
  /* eslint-enable @typescript-eslint/no-unused-vars */

  const strEndDdd2 = apiPayload.strEndDdd2 ?? strResDDD2 ?? "";
  const strEndFone2 = apiPayload.strEndFone2 ?? strResFone2 ?? "";
  const strEndTipoFone2 = normalizeResidentialPhone2TipoToApiCode(
    apiPayload.strEndTipoFone2 ?? strResTipoFone2
  );
  const primaryPhone = joinPhoneDigits(
    apiPayload.strEndDdd1,
    apiPayload.strEndFone1
  );
  const additionalPhone = joinPhoneDigits(strEndDdd2, strEndFone2);
  const shouldOmitAdditionalPhone = areNormalizedPhonesEqual(
    primaryPhone,
    additionalPhone
  );
  const datAtivDataAdmissao = pickFirstValidErpDate(
    rawDatAtivDataAdmissao,
    strAtivDataAdmissao,
    datTrabDesde,
    strTrabalhaDesde
  );
  const datNascimento = normalizeBirthDateToBackendDate(rawDatNascimento);

  return stripUndefinedValues({
    ...stripUndefinedValues(apiPayload),
    datNascimento,
    datAtivDataAdmissao,
    strEndDdd2: shouldOmitAdditionalPhone ? "" : strEndDdd2,
    strEndFone2: shouldOmitAdditionalPhone ? "" : strEndFone2,
    strEndTipoFone2: shouldOmitAdditionalPhone ? "" : strEndTipoFone2,
    strEndTipoFone1: TIPO_RESIDENCIA.RESIDENCIAL,
    strResTipoFone1: TIPO_RESIDENCIA.RESIDENCIAL,
    strNomeMae: sanitizeParentNameForApi(apiPayload.strNomeMae),
    strNomePai: sanitizeParentNameForApi(apiPayload.strNomePai)
  });
};
